// ============================================================================
// ACCT-CASES-001 — Account Cases (Actionable Accounts list)
// ============================================================================
// One row per actionable account with per-category case counts. Row click /
// "View 360" opens AccountCases360Modal. Phase N1: list + 360. Phase N2 wires
// Import Batch (Excel upload) and Run Eligibility (engine sweep) — buttons
// are wired through here as toasts until then.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useActionableAccounts } from 'hooks/api/useAccountCases';
import { preLitigationService } from 'api/services/preLitigationService';
import type {
  ActionableAccountRow,
  ActionableAccountSearchRequest,
} from 'api/types/accountCases.types';
import AccountCases360Modal from 'components/modals/modules/account-cases-module/AccountCases360Modal';
import AccountImportModal from 'components/modals/modules/account-cases-module/AccountImportModal';

const SCOPE_OPTIONS: { value: string; label: string }[] = [
  { value: '',           label: 'Actionable (default)' },
  { value: 'WITH_CASES', label: 'Has cases only' },
  { value: 'CANDIDATES', label: 'No cases yet (eligible)' },
  { value: 'ALL',        label: 'All (debug)' },
];

const NPA_OPTIONS: { value: string; label: string }[] = [
  { value: '',      label: 'Any NPA flag' },
  { value: 'true',  label: 'NPA only' },
  { value: 'false', label: 'Non-NPA only' },
];

const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const fmtAmount = (n?: number | null) =>
  n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const AccountCasesScreen: React.FC = () => {
  const [queryParams, setQueryParams] = useState<ActionableAccountSearchRequest>({
    page: 1, pageSize: 25, scope: 'ACTIONABLE', sortBy: 'CurrentDpd', sortOrder: 'desc',
  });
  const { data, loading, pagination, refetch } = useActionableAccounts(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [scopeFilter, setScopeFilter] = useState<string>('');
  const [productFilter, setProductFilter] = useState<string>('');
  const [npaFilter, setNpaFilter] = useState<string>('');
  const [minDpdFilter, setMinDpdFilter] = useState<string>('');

  // 360 modal state
  const [openAccountId, setOpenAccountId] = useState<string | null>(null);
  // Import modal state
  const [importOpen, setImportOpen] = useState(false);
  // Run-eligibility state
  const [isRunningEligibility, setIsRunningEligibility] = useState(false);

  // Debounced search → API
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery.trim() || undefined, page: 1 })),
      400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange     = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(p => ({
      ...p,
      scope:       scopeFilter || undefined,
      productCode: productFilter.trim() || undefined,
      isNpa:       npaFilter === '' ? undefined : npaFilter === 'true',
      minDpd:      minDpdFilter ? Number(minDpdFilter) : undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [scopeFilter, productFilter, npaFilter, minDpdFilter]);

  const handleClearFilters = useCallback(() => {
    setScopeFilter(''); setProductFilter(''); setNpaFilter(''); setMinDpdFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25, scope: 'ACTIONABLE', sortBy: 'CurrentDpd', sortOrder: 'desc' });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (queryParams.scope && queryParams.scope !== 'ACTIONABLE') n++;
    if (queryParams.productCode) n++;
    if (queryParams.isNpa !== undefined) n++;
    if (queryParams.minDpd != null) n++;
    return n;
  }, [queryParams]);

  // ---- Row click & menu actions ----

  const handleOpen360 = useCallback((row: ActionableAccountRow) => {
    setOpenAccountId(String(row.accountId));
  }, []);

  const getRowActions = useCallback((r: ActionableAccountRow): ActionMenuItem[] => [
    { id: 'view360', label: 'View 360',  icon: <IconSystem.list size={16} />, onClick: () => handleOpen360(r) },
    { id: 'eligibility', label: 'Open Eligibility Report', icon: <IconSystem.search size={16} />,
      onClick: () => notificationService.toast.info('Eligibility report — coming next cycle.') },
  ], [handleOpen360]);

  // ---- Top action buttons ----

  const handleImportBatch = useCallback(() => {
    setImportOpen(true);
  }, []);

  const handleRunEligibility = useCallback(async () => {
    if (isRunningEligibility) return;
    setIsRunningEligibility(true);
    notificationService.toast.info('Running eligibility across all configured case types…', 4000);
    try {
      // Empty request → backend sweeps every registered eligibility rule
      // (PRE_LITIGATION, SARFAESI, …) against the current account snapshot.
      const result = await preLitigationService.recomputePool({});
      const byType = result.data?.byCaseType ?? {};

      const parts = Object.entries(byType)
        .filter(([, v]) => (v?.added ?? 0) > 0)
        .map(([code, v]) => `${code}: ${v.added}`);
      const totalAdded = Object.values(byType).reduce((sum, v) => sum + (v?.added ?? 0), 0);

      if (totalAdded > 0) {
        notificationService.toast.success(
          `Eligibility run complete — ${totalAdded} new case${totalAdded === 1 ? '' : 's'} added (${parts.join(', ')}).`,
          6000,
        );
      } else {
        notificationService.toast.info('Eligibility run complete — no new eligible accounts found.', 5000);
      }
      refetch();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Eligibility run failed. Please try again.';
      notificationService.toast.error(msg, 6000);
    } finally {
      setIsRunningEligibility(false);
    }
  }, [isRunningEligibility, refetch]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'import',  icon: <IconSystem.importIcon size={18} />, label: 'Import Batch',
      tooltip: 'Upload a loan-accounts Excel and run eligibility',
      onClick: handleImportBatch, variant: 'icon' },
    { id: 'runElig', icon: <IconSystem.checkCircle size={18} />,
      label: isRunningEligibility ? 'Running…' : 'Run Eligibility',
      tooltip: 'Re-evaluate eligibility across all loaded accounts and all configured case types',
      onClick: handleRunEligibility, variant: 'icon', disabled: isRunningEligibility },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh',
      tooltip: 'Reload list', onClick: () => refetch(), variant: 'icon' },
  ], [handleImportBatch, handleRunEligibility, refetch, isRunningEligibility]);

  // ---- Columns ----

  const columns: SyutGridColumn<ActionableAccountRow>[] = useMemo(() => [
    { id: 'accountNumber', header: 'Account #', accessorKey: 'accountNumber', size: 150, enableSorting: true,
      cell: ({ row }) => <strong>{row.original.accountNumber}</strong>,
    },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 220, enableSorting: true,
      cell: ({ row }) => row.original.customerName ?? <span style={{ color: 'var(--text-muted)' }}>—</span> },
    { id: 'branchCode',   header: 'Branch',   accessorKey: 'branchCode',   size: 90,  enableSorting: true,
      cell: ({ row }) => row.original.branchCode ?? '—' },
    { id: 'productCode',  header: 'Product',  accessorKey: 'productCode',  size: 90,  enableSorting: true,
      cell: ({ row }) => row.original.productCode ?? '—' },
    { id: 'currentOutstanding', header: 'Outstanding', accessorKey: 'currentOutstanding', size: 130, enableSorting: true,
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
          {fmtAmount(row.original.currentOutstanding)}
        </span>
      ),
    },
    { id: 'currentDpd',   header: 'DPD',      accessorKey: 'currentDpd',   size: 70,  enableSorting: true },
    { id: 'isNpa',        header: 'NPA',      accessorKey: 'isNpa',        size: 70,
      cell: ({ row }) => row.original.isNpa
        ? <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#dc2626', border: '1px solid #dc2626', padding: '1px 6px', borderRadius: 999 }}>NPA</span>
        : <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    { id: 'cases', header: 'Cases', accessorKey: 'totalCaseCount', size: 220, enableSorting: false,
      cell: ({ row }) => <CaseBadges r={row.original} />,
    },
    { id: 'lastUpdatedAt', header: 'Last Updated', accessorKey: 'lastUpdatedAt', size: 120, enableSorting: true,
      cell: ({ row }) => fmtDate(row.original.lastUpdatedAt),
    },
  ], [handleOpen360]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Actionable Accounts"
        description="Actionable accounts — those with at least one case or past a DPD threshold. Open the 360 view to drill into the full case history per account."
        icon={<IconSystem.list size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search account # / customer…',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              isLoading={loading}
              emptyMessage="No actionable accounts."
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => { handleClearFilters(); setFilterOpen(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-muted)' }}
                className="search-panel-close" title="Close filters">
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Scope"
                  options={SCOPE_OPTIONS}
                  value={scopeFilter}
                  onChange={(v) => setScopeFilter((v as string) || '')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Product Code</label>
                <input type="text" className="form-input"
                  value={productFilter}
                  placeholder="e.g. HL, VL, PL"
                  onChange={(e) => setProductFilter(e.target.value.toUpperCase())}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect label="NPA"
                  options={NPA_OPTIONS}
                  value={npaFilter}
                  onChange={(v) => setNpaFilter((v as string) || '')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Min DPD</label>
                <input type="number" className="form-input"
                  value={minDpdFilter} min={0}
                  onChange={(e) => setMinDpdFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
              <button className="btn btn-primary"   onClick={handleApplyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <AccountCases360Modal
        isOpen={!!openAccountId}
        accountId={openAccountId}
        onClose={() => setOpenAccountId(null)}
      />

      <AccountImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => refetch()}
      />
    </div>
  );
};

// ----------------------------------------------------------------------------
// CaseBadges — three small badges per category. Empty if count is zero.
// ----------------------------------------------------------------------------

const CaseBadges: React.FC<{ r: ActionableAccountRow }> = ({ r }) => {
  if (r.totalCaseCount === 0) {
    return <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>—</span>;
  }
  return (
    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
      {r.earlyRecoveryCount > 0 && <Badge label="Early"     count={r.earlyRecoveryCount} color="#2563eb" />}
      {r.recoveryCount      > 0 && <Badge label="Recovery"  count={r.recoveryCount}      color="#16a34a" />}
      {r.litigationCount    > 0 && <Badge label="Litigation" count={r.litigationCount}   color="#d97706" />}
    </div>
  );
};

const Badge: React.FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => (
  <span style={{
    fontSize: '0.7rem', fontWeight: 600, color,
    border: `1px solid ${color}`, padding: '1px 6px', borderRadius: 999,
    whiteSpace: 'nowrap',
  }}>
    {label} <strong style={{ marginLeft: 2 }}>{count}</strong>
  </span>
);

export default AccountCasesScreen;
